import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { classifyStudent, daysSinceLastSession, RetentionCategory } from '@/lib/retention';

export interface StudentRetentionData {
  category: RetentionCategory;
  daysSinceLast: number | null;
  sessionsThisWeek: number;
  sessionsThisMonth: number;
  weeklyHistory: { weekStart: string; count: number }[]; // últimas 8 semanas (mais antigo → mais novo)
}

const WEEKS = 8;
const DAY_MS = 86_400_000;

export function useStudentRetention(studentId: string) {
  return useQuery({
    queryKey: ['student-retention', studentId],
    queryFn: async (): Promise<StudentRetentionData> => {
      const since = new Date(Date.now() - 90 * DAY_MS).toISOString();
      const { data, error } = await supabase
        .from('workout_sessions')
        .select('completed_at')
        .eq('student_id', studentId)
        .gte('completed_at', since)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false });
      if (error) throw error;

      const sessions = (data ?? []) as { completed_at: string }[];
      const now = Date.now();
      const category = classifyStudent(sessions);
      const daysSinceLast = daysSinceLastSession(sessions);

      const sevenDaysAgo = now - 7 * DAY_MS;
      const thirtyDaysAgo = now - 30 * DAY_MS;
      const sessionsThisWeek = sessions.filter(s => new Date(s.completed_at).getTime() >= sevenDaysAgo).length;
      const sessionsThisMonth = sessions.filter(s => new Date(s.completed_at).getTime() >= thirtyDaysAgo).length;

      // Histórico semanal (últimas 8 segundas-feiras pra trás)
      const lastMonday = new Date();
      const day = lastMonday.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      lastMonday.setDate(lastMonday.getDate() + diff);
      lastMonday.setHours(0, 0, 0, 0);

      const weeklyHistory: { weekStart: string; count: number }[] = [];
      for (let i = WEEKS - 1; i >= 0; i--) {
        const weekStart = new Date(lastMonday);
        weekStart.setDate(weekStart.getDate() - i * 7);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 7);
        const count = sessions.filter(s => {
          const t = new Date(s.completed_at).getTime();
          return t >= weekStart.getTime() && t < weekEnd.getTime();
        }).length;
        weeklyHistory.push({ weekStart: weekStart.toISOString().slice(0, 10), count });
      }

      return { category, daysSinceLast, sessionsThisWeek, sessionsThisMonth, weeklyHistory };
    },
    enabled: !!studentId,
    staleTime: 60 * 1000,
  });
}
